import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, MinusCircle, Loader2, Building2, Flag } from "lucide-react";
import api from "@/lib/api";

const STATUS_OPT = [
  { value: "all", label: "Tous" },
  { value: "pending", label: "En attente" },
  { value: "approved", label: "Validées" },
  { value: "rejected", label: "Refusées" },
  { value: "revoked", label: "Révoquées" },
];
const TYPE_OPT = [
  { value: "all", label: "Tous" },
  { value: "club", label: "Clubs" },
  { value: "federation", label: "Fédérations" },
];

export default function AdminEntityManagersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [typeFlt, setTypeFlt] = useState("all");
  const [decisionMgr, setDecisionMgr] = useState(null);
  const [decisionType, setDecisionType] = useState(null); // approve|reject|revoke
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (typeFlt !== "all") params.entity_type = typeFlt;
      const res = await api.get("/admin/entity-managers", { params });
      setItems(res.data?.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status, typeFlt]);

  const decide = async () => {
    if (!decisionMgr || !decisionType) return;
    setBusy(true);
    try {
      await api.post(`/admin/entity-managers/${decisionMgr.id}/${decisionType}`, { reason: reason || null });
      toast.success("Décision enregistrée");
      setDecisionMgr(null); setDecisionType(null); setReason("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-entity-managers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
            Revendications de gestion
          </h1>
          <p className="text-sm text-muted-foreground">Clubs & fédérations : approuvez, refusez ou révoquez les accès.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="admin-em-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPT.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={typeFlt} onValueChange={setTypeFlt}>
            <SelectTrigger className="w-40" data-testid="admin-em-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPE_OPT.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <p className="p-6 text-sm text-muted-foreground">Chargement…</p> : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground italic">Aucune revendication.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-em-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Demandeur</TableHead>
                    <TableHead>Structure</TableHead>
                    <TableHead>Fonction</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} className="hover:bg-muted/50" data-testid={`admin-em-row-${it.id}`}>
                      <TableCell>
                        <div className="font-medium">{it.user?.name || it.user?.email || it.user_id?.slice(0,8)}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{(it.user?.roles || []).join(", ")}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {it.entity_type === "club" ? <Building2 className="h-4 w-4" /> : <Flag className="h-4 w-4" />}
                          {it.entity?.name || it.entity_id?.slice(0,8)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{it.role_label || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className="uppercase text-[10px] tracking-wide">{it.status}</Badge></TableCell>
                      <TableCell className="text-xs font-mono">{new Date(it.created_at || it.requested_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {it.status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setDecisionMgr(it); setDecisionType("approve"); }} data-testid={`admin-em-approve-${it.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approuver
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setDecisionMgr(it); setDecisionType("reject"); }} data-testid={`admin-em-reject-${it.id}`}>
                              <XCircle className="h-3.5 w-3.5 mr-1" /> Refuser
                            </Button>
                          </>
                        )}
                        {it.status === "approved" && (
                          <Button size="sm" variant="outline" onClick={() => { setDecisionMgr(it); setDecisionType("revoke"); }} data-testid={`admin-em-revoke-${it.id}`}>
                            <MinusCircle className="h-3.5 w-3.5 mr-1" /> Révoquer
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!decisionMgr} onOpenChange={(o) => { if (!o) { setDecisionMgr(null); setDecisionType(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionType === "approve" ? "Approuver la revendication"
                : decisionType === "reject" ? "Refuser la revendication"
                : "Révoquer l'accès"}
            </DialogTitle>
            <DialogDescription>
              {decisionMgr?.user?.name || decisionMgr?.user_id?.slice(0,8)} — {decisionMgr?.entity?.name}
            </DialogDescription>
          </DialogHeader>
          {(decisionType === "reject" || decisionType === "revoke") && (
            <Textarea placeholder="Motif (optionnel)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-em-reason" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionMgr(null)} disabled={busy}>Annuler</Button>
            <Button onClick={decide} disabled={busy} data-testid="admin-em-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
